import { useCallback, useEffect, useState } from 'react';
import servicesApi from '@src/@data/api/services';
import calendarApi from '@src/@data/api/calendars';
import { toastOnError } from '@src/@data/utility/errors';
import { BusinessCalendar } from '@src/@data/types/calendars';
import { BusinessService } from '@src/@data/types/services';
import { defaultCalendarsSort } from '@src/@data/utility/calendars';
import { defaultServiceSort } from '@src/@data/utility/services';

export interface CreateReservationHook {
  isLoading: boolean;
  availableCalendars: BusinessCalendar[];
  availableServices: BusinessService[];
  selectedService: BusinessService | null;
  selectedCalendar: BusinessCalendar | null;
  setSelectedService: (service: BusinessService | null) => void;
  setSelectedCalendar: (calendar: BusinessCalendar | null) => void;
}

export const useCreateReservation = (
  companyId: string,
  serviceId?: string,
  calendarId?: string
): CreateReservationHook => {
  const [isLoading, setIsLoading] = useState(false);
  const [allCalendars, setAllCalendars] = useState<BusinessCalendar[]>([]);
  const [allServices, setAllServices] = useState<BusinessService[]>([]);

  const [availableCalendars, setAvailableCalendars] = useState<
    BusinessCalendar[]
  >([]);
  const [availableServices, setAvailableServices] = useState<BusinessService[]>(
    []
  );

  const [selectedCalendar, setSelectedCalendar] =
    useState<BusinessCalendar | null>(null);
  const [selectedService, setSelectedService] =
    useState<BusinessService | null>(null);

  const setServiceInternal = useCallback(
    (calendars: BusinessCalendar[], service: BusinessService | null) => {
      if (!!serviceId && service?.id !== serviceId) {
        return;
      }
      if (service === null) {
        setSelectedService(null);
        setAvailableCalendars(calendars.sort(defaultCalendarsSort));
        return;
      }
      setSelectedService(service);
      const newCalendars = calendars.filter((c) =>
        c.services?.some((s) => s.serviceId === service.id)
      );
      setAvailableCalendars(newCalendars.sort(defaultCalendarsSort));
      setSelectedCalendar((currentCalendar) => {
        if (newCalendars.length === 1) {
          return newCalendars[0];
        }
        if (
          currentCalendar !== null &&
          !newCalendars.some((c) => c.id === currentCalendar.id)
        ) {
          return null;
        }
        return currentCalendar;
      });
    },
    [serviceId]
  );

  const setCalendarInternal = useCallback(
    (services: BusinessService[], calendar: BusinessCalendar | null) => {
      if (!!calendarId && calendar?.id !== calendarId) {
        return;
      }
      if (calendar === null) {
        setSelectedCalendar(null);
        setAvailableServices(services.sort(defaultServiceSort));
        return;
      }
      setSelectedCalendar(calendar);
      const newServices = services.filter((as) =>
        calendar.services?.some((s) => s.serviceId === as.id)
      );
      setAvailableServices(newServices.sort(defaultServiceSort));
      setSelectedService((currentService) => {
        if (newServices.length === 1) {
          return newServices[0];
        }
        if (
          currentService !== null &&
          !newServices.some((s) => s.id === currentService.id)
        ) {
          return null;
        }
        return currentService;
      });
    },
    [calendarId]
  );

  const setService = useCallback(
    (service: BusinessService | null) => {
      setServiceInternal(allCalendars, service);
    },
    [allCalendars, setServiceInternal]
  );

  const setCalendar = useCallback(
    (calendar: BusinessCalendar | null) => {
      setCalendarInternal(allServices, calendar);
    },
    [allServices, setCalendarInternal]
  );

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      servicesApi.getBusinessServices(companyId),
      calendarApi.getBusinessCalendars(companyId),
    ])
      .then(([services, calendars]) => {
        setAllCalendars(calendars);
        setAllServices(services);
        if (serviceId) {
          setServiceInternal(
            calendars,
            services.find((s) => s.id === serviceId) || null
          );
        }
        if (calendarId) {
          setCalendarInternal(
            services,
            calendars.find((c) => c.id === calendarId) || null
          );
        }
        if (!serviceId && !calendarId) {
          setAvailableCalendars(calendars.sort(defaultCalendarsSort));
          setAvailableServices(services.sort(defaultServiceSort));
        }
      })
      .catch((error) => {
        toastOnError(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    calendarId,
    companyId,
    serviceId,
    setCalendarInternal,
    setServiceInternal,
  ]);

  return {
    isLoading,
    availableCalendars,
    availableServices,
    selectedService,
    selectedCalendar,
    setSelectedService: setService,
    setSelectedCalendar: setCalendar,
  };
};
